// Client-side data export. CSV is built here (no dependency); the XLSX and
// PDF paths lazy-import their libraries so they never touch the main bundle.

export interface Column<T> {
  label: string;
  get: (row: T) => string | number | null | undefined;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T>(rows: T[], columns: Column<T>[]): string {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(c.get(r))).join(',')).join('\r\n');
  return `${head}\r\n${body}${rows.length ? '\r\n' : ''}`;
}

export function downloadCsv<T>(rows: T[], columns: Column<T>[], filename: string) {
  // BOM so Excel opens it as UTF-8.
  const blob = new Blob(['﻿' + toCsv(rows, columns)], {
    type: 'text/csv;charset=utf-8',
  });
  downloadBlob(blob, filename);
}

export interface Sheet<T> {
  name: string;
  rows: T[];
  columns: Column<T>[];
}

/** Multi-sheet .xlsx via write-excel-file (lazy-loaded). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function downloadXlsx(sheets: Sheet<any>[], filename: string) {
  const writeXlsxFile = (await import('write-excel-file/browser')).default;
  const sheetArgs = sheets.map((s) => {
    const header = s.columns.map((c) => ({ value: c.label, fontWeight: 'bold' as const }));
    const body = s.rows.map((r) =>
      s.columns.map((c) => {
        const v = c.get(r);
        return typeof v === 'number'
          ? { type: Number, value: v }
          : { type: String, value: v == null ? '' : String(v) };
      }),
    );
    return {
      sheet: s.name,
      data: [header, ...body],
      columns: s.columns.map(() => ({ width: 22 })),
    };
  });
  const blob = await writeXlsxFile(sheetArgs as never).toBlob();
  downloadBlob(blob, filename);
}

const today = () => new Date().toISOString().slice(0, 10);
export const stamp = today;

/** yes/no from a boolean, blank from null/undefined. */
export const yn = (v: boolean | null | undefined) => (v == null ? '' : v ? 'Yes' : 'No');

function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, rej) => setTimeout(() => rej(new Error(`${what} timed out`)), ms)),
  ]);
}

type JsPdf = import('jspdf').jsPDF;

async function renderCanvas(node: HTMLElement) {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await withTimeout(
    html2canvas(node, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true }),
    30_000,
    'Rendering the chart',
  );
  if (!canvas.width || !canvas.height) throw new Error('The chart rendered empty.');
  return canvas;
}

/** Lay one rendered canvas onto `pdf`, splitting across as many pages as it
 *  needs. `startsNewPage` adds a page break before the first slice (used when
 *  this isn't the first section in the document). */
function placeCanvas(
  pdf: JsPdf,
  canvas: HTMLCanvasElement,
  title: string | undefined,
  startsNewPage: boolean,
) {
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const headerH = title ? 18 : 0;
  const contentTop = margin + headerH;

  const usableW = pageW - margin * 2;
  const scale = usableW / canvas.width;
  const scaledH = canvas.height * scale;
  const pageContentH = Math.max(60, pageH - contentTop - margin);
  const pages = Math.max(1, Math.min(50, Math.ceil(scaledH / pageContentH)));

  const header = () => {
    if (!title) return;
    pdf.setFontSize(10);
    pdf.setTextColor(110);
    pdf.text(title, margin, margin + 9);
  };

  for (let page = 0; page < pages; page++) {
    if (page > 0 || startsNewPage) pdf.addPage();
    header();
    // The whole image, shifted up so this page shows the next slice.
    pdf.addImage(dataUrl, 'JPEG', margin, contentTop - page * pageContentH, usableW, scaledH);
    // jsPDF doesn't clip addImage to the page — paint over the overflow, then
    // (re)draw the header above the mask.
    if (pages > 1) {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageW, contentTop, 'F');
      pdf.rect(0, contentTop + pageContentH, pageW, pageH, 'F');
      header();
    }
  }
}

/** Render a DOM node to a one-or-more-page PDF, fit to page width. Lazy-loads
 *  jspdf + html2canvas. */
export async function nodeToPdf(node: HTMLElement, filename: string, title?: string) {
  const { jsPDF } = await import('jspdf');
  const canvas = await renderCanvas(node);
  const landscape = canvas.width > canvas.height * 1.1;
  const pdf = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'pt',
    format: 'a4',
  });
  placeCanvas(pdf, canvas, title, false);
  pdf.save(filename);
}

/** Render several DOM nodes into one portrait PDF, each section starting on a
 *  fresh page (and spilling onto more pages if it's taller than one). Used for
 *  the all-districts org chart — one district per page, where packing two
 *  districts onto one page would read as one merged chart. */
export async function nodesToPdf(
  sections: { node: HTMLElement; title?: string }[],
  filename: string,
) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  let placed = 0;
  for (const s of sections) {
    let canvas: HTMLCanvasElement;
    try {
      canvas = await renderCanvas(s.node);
    } catch {
      continue; // skip a section that renders empty rather than fail the whole file
    }
    placeCanvas(pdf, canvas, s.title, placed > 0);
    placed++;
  }
  if (placed === 0) throw new Error('The chart rendered empty.');
  pdf.save(filename);
}

/** Render several DOM nodes into one portrait PDF, packing each section under
 *  the last if it fits on the current page and starting a fresh page only
 *  when it doesn't — unlike `nodesToPdf`, which always starts a new page per
 *  section. One shared `title` header (not per-section) is redrawn on every
 *  page a new page starts. A section taller than one page still spills across
 *  as many as it needs, on its own fresh page(s). Used for reports that
 *  should read as one flowing document (FOS's summary + by-unit table),
 *  rather than one-thing-per-page (org chart's districts). */
export async function flowSectionsToPdf(
  sections: HTMLElement[],
  filename: string,
  title?: string,
) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const headerH = title ? 18 : 0;
  const contentTop = margin + headerH;
  const pageContentH = pageH - contentTop - margin;
  const gap = 14; // points between packed sections on the same page

  const drawHeader = () => {
    if (!title) return;
    pdf.setFontSize(10);
    pdf.setTextColor(110);
    pdf.text(title, margin, margin + 9);
  };

  let cursorY = contentTop;
  let pageStarted = false;
  let placedAny = false;

  for (const node of sections) {
    let canvas: HTMLCanvasElement;
    try {
      canvas = await renderCanvas(node);
    } catch {
      continue;
    }
    const usableW = pageW - margin * 2;
    const scale = usableW / canvas.width;
    const scaledH = canvas.height * scale;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    if (scaledH > pageContentH) {
      // Taller than one page by itself — give it its own fresh page(s),
      // sliced the same way a single big section always has been.
      if (pageStarted) pdf.addPage();
      drawHeader();
      const pages = Math.max(1, Math.min(50, Math.ceil(scaledH / pageContentH)));
      for (let page = 0; page < pages; page++) {
        if (page > 0) {
          pdf.addPage();
          drawHeader();
        }
        pdf.addImage(dataUrl, 'JPEG', margin, contentTop - page * pageContentH, usableW, scaledH);
        if (pages > 1) {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageW, contentTop, 'F');
          pdf.rect(0, contentTop + pageContentH, pageW, pageH, 'F');
          drawHeader();
        }
      }
      cursorY = pageH; // force whatever comes next onto a fresh page
      pageStarted = true;
      placedAny = true;
      continue;
    }

    // Fits on one page — pack it under the previous section if there's
    // room left; otherwise start a fresh page rather than drawing a
    // cramped sliver at the bottom of this one.
    if (!pageStarted || cursorY + scaledH > pageH - margin) {
      if (pageStarted) pdf.addPage();
      drawHeader();
      cursorY = contentTop;
      pageStarted = true;
    }
    pdf.addImage(dataUrl, 'JPEG', margin, cursorY, usableW, scaledH);
    cursorY += scaledH + gap;
    placedAny = true;
  }

  if (!placedAny) throw new Error('The report rendered empty.');
  pdf.save(filename);
}
